// Phase 474 — Finance ↔ Cleaning / Building sync audit & repair UI.
// Read-only audit view + admin repair action. Designed to be the
// single-source-of-truth dashboard users open whenever they suspect
// Finance and operational modules have drifted apart.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowsClockwise, CheckCircle, Warning, Bug, ArrowSquareOut, FlowArrow } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `€${(Number(n) || 0).toFixed(2)}`;

const Stat = ({ label, value, sub, tone = 'neutral', testid }) => {
  const tones = {
    neutral: 'border-zinc-200 bg-white text-zinc-800',
    good:    'border-emerald-200 bg-emerald-50 text-emerald-900',
    warn:    'border-amber-200 bg-amber-50 text-amber-900',
    bad:     'border-red-200 bg-red-50 text-red-900',
    info:    'border-blue-200 bg-blue-50 text-blue-900',
  };
  return (
    <div className={`px-4 py-3 border ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-mono font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <section className="bg-white border border-zinc-200" data-testid={testid}>
    <header className="px-4 py-2 border-b border-zinc-200 bg-zinc-50">
      <h3 className="text-sm font-semibold text-zinc-800">{title}</h3>
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const IssueList = ({ label, count, sum, examples, onResync, testid, kind }) => {
  const [open, setOpen] = useState(false);
  if (count === 0) {
    return (
      <div className="border border-emerald-200 bg-emerald-50/60 px-4 py-2 flex items-center justify-between" data-testid={testid}>
        <div className="flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-600" />
          <span className="text-sm font-medium text-emerald-800">{label}</span>
        </div>
        <span className="text-xs text-emerald-700 font-mono">0</span>
      </div>
    );
  }
  return (
    <div className="border border-amber-200 bg-amber-50/40" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-amber-100/40"
      >
        <div className="flex items-center gap-2">
          <Warning size={16} className="text-amber-700" />
          <span className="text-sm font-semibold text-amber-900">{label}</span>
          {sum != null && <span className="text-[11px] text-amber-700">{fmtMoney(sum)}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-bold text-amber-900 bg-white border border-amber-300 px-2 py-0.5">{count}</span>
          <span className="text-amber-600 text-xs">{open ? '−' : '+'}</span>
        </div>
      </button>
      {open && examples && examples.length > 0 && (
        <ul className="border-t border-amber-200 divide-y divide-amber-100/80 bg-white" data-testid={`${testid}-examples`}>
          {examples.map((ex, i) => {
            const rowId = ex.payment_id || ex.income_id || `row-${i}`;
            return (
              <li key={rowId} className="px-4 py-2 flex items-center gap-3 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-zinc-500 text-[11px]">{ex.date} · {rowId}</div>
                  <div className="text-zinc-700 truncate mt-0.5">{ex.description || ex.notes || ex.client_id || ex.building_id || '—'}</div>
                </div>
                <div className="font-mono font-semibold text-zinc-800 w-20 text-right">{fmtMoney(ex.amount)}</div>
                {onResync && kind && (ex.payment_id) && (
                  <button
                    onClick={() => onResync(kind, ex.payment_id, ex)}
                    className="text-blue-700 hover:underline text-[11px] font-medium whitespace-nowrap inline-flex items-center gap-1"
                    data-testid={`resync-${rowId}`}
                  >
                    <ArrowsClockwise size={11} /> Resync
                  </button>
                )}
              </li>
            );
          })}
          {examples.length === 25 && (
            <li className="px-4 py-1.5 text-[11px] text-amber-700 bg-amber-50">Showing first 25 examples. Run repair to fix all.</li>
          )}
        </ul>
      )}
    </div>
  );
};

const SyncAudit = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  // Phase 481 — Duplicate client detection.
  const [dupes, setDupes] = useState(null);
  const [merging, setMerging] = useState(null); // group key currently being merged

  const fetchAudit = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [auditR, dupR] = await Promise.all([
        axios.get(`${API}/sync/finance-audit?include_examples=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/cleaning/clients/duplicates`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { duplicates: [], total_groups: 0 } })),
      ]);
      setData(auditR.data);
      setDupes(dupR.data);
    } catch (e) {
      console.error('[SyncAudit] fetch failed', e);
      toast.error(e?.response?.data?.detail || 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const runRepair = async () => {
    if (repairing) return;
    setRepairing(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/sync/finance-repair`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cl = r.data?.cleaning || {};
      const bd = r.data?.building || {};
      toast.success(`Repair complete · ${cl.created || 0} cleaning · ${bd.created || 0} building · ${r.data?.relinks || 0} relinks`);
      await fetchAudit();
    } catch (e) {
      console.error('[SyncAudit] repair failed', e);
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const resyncOne = async (kind, paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const body = kind === 'building' ? { building_payment_id: paymentId } : { cleaning_payment_id: paymentId };
      const r = await axios.post(`${API}/sync/finance-repair/single`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${r.data?.status || 'OK'} → ${r.data?.income_id || ''}`);
      fetchAudit();
    } catch (e) {
      console.error('[SyncAudit] resync failed', e);
      toast.error(e?.response?.data?.detail || 'Resync failed');
    }
  };

  // Phase 481 — Merge a duplicate client pair. The biggest-data client
  // is the survivor by default but the user can choose. We always require
  // an explicit user click to be safe — never auto-merge.
  const mergeDuplicate = async (survivorId, victimId, groupKey) => {
    if (merging) return;
    if (!survivorId || !victimId || survivorId === victimId) return;
    if (!window.confirm(`Merge ${victimId} INTO ${survivorId}? All records will be re-pointed. The source will be soft-deleted with a merged_into reference.`)) {
      return;
    }
    setMerging(groupKey);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/cleaning/clients/${victimId}/merge-into/${survivorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const moved = r.data?.moved || {};
      const totalMoved = Object.values(moved).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Merged · ${totalMoved} records moved · source archived`);
      fetchAudit();
    } catch (e) {
      console.error('[SyncAudit] merge failed', e);
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-zinc-400" data-testid="sync-audit-loading">{t('loading') || 'Loading…'}</div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-red-600" data-testid="sync-audit-error">Failed to load audit.</div>;
  }

  const f = data.finance;
  const c = data.cleaning_payments;
  const b = data.building_payments;
  const r = data.reconciliation;
  const h = data.health;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="sync-audit-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <FlowArrow size={26} className="text-zinc-700" />
            {t('syncAudit') || 'Sync Audit'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {t('syncAuditHint') || 'Compare Finance Bank Accounts with Cleaning & Building payments. Detect orphans, broken links and pending syncs — then repair safely.'}
          </p>
          <p className="text-[11px] text-zinc-400 mt-1 font-mono">{t('generated') || 'Generated'}: {new Date(data.generated_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchAudit} variant="outline" size="sm" className="rounded-none" data-testid="audit-refresh-btn">
            <ArrowsClockwise size={14} className="mr-1" /> {t('refresh') || 'Refresh'}
          </Button>
          <Button onClick={runRepair} disabled={repairing} size="sm" className="rounded-none" data-testid="audit-repair-btn">
            <Bug size={14} className="mr-1" /> {repairing ? (t('repairing') || 'Repairing…') : (t('runRepair') || 'Run Repair')}
          </Button>
        </div>
      </header>

      {/* Top health banner */}
      <div
        className={`p-4 border ${h.is_clean ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'} flex items-center gap-3`}
        data-testid="health-banner"
      >
        {h.is_clean ? <CheckCircle size={28} className="text-emerald-600" /> : <Warning size={28} className="text-amber-600" />}
        <div className="flex-1">
          <p className={`font-semibold ${h.is_clean ? 'text-emerald-900' : 'text-amber-900'}`}>
            {h.is_clean
              ? (t('allInSync') || 'Everything is in sync.')
              : `${h.issues} ${t('issuesFound') || 'issue(s) found across modules'}`}
          </p>
          <p className={`text-xs mt-0.5 ${h.is_clean ? 'text-emerald-700' : 'text-amber-700'}`}>
            {t('reconciliationStatus') || 'Reconciliation'}: {h.reconciliation_clean ? '✓ Clean' : `Δ cleaning ${fmtMoney(r.delta_cleaning)} · Δ building ${fmtMoney(r.delta_building)} (VAT differences expected)`}
          </p>
        </div>
      </div>

      {/* Reconciliation totals */}
      <Section title={t('reconciliationTotals') || 'Reconciliation Totals'} testid="section-reconciliation">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label={t('financeTotal') || 'Finance income (active)'}
            value={fmtMoney(f.sum)}
            sub={`${f.total} ${t('rows') || 'rows'}`}
            testid="stat-finance-total"
          />
          <Stat
            label={t('syncedToCleaning') || 'Synced ↔ Cleaning'}
            value={fmtMoney(f.synced_to_cleaning.sum)}
            sub={`${f.synced_to_cleaning.count} ${t('rows') || 'rows'}`}
            tone="info"
            testid="stat-synced-cleaning"
          />
          <Stat
            label={t('syncedToBuilding') || 'Synced ↔ Building'}
            value={fmtMoney(f.synced_to_building.sum)}
            sub={`${f.synced_to_building.count} ${t('rows') || 'rows'}`}
            tone="info"
            testid="stat-synced-building"
          />
          <Stat
            label={t('manualIncomes') || 'Manual (no link)'}
            value={fmtMoney(f.manual.sum)}
            sub={`${f.manual.count} ${t('rows') || 'rows'}`}
            testid="stat-manual"
          />
        </div>
      </Section>

      {/* Finance anomalies */}
      <Section title={t('financeAnomalies') || 'Finance anomalies'} testid="section-finance-anomalies">
        <div className="space-y-2">
          <IssueList
            label={t('orphanCleaningLink') || 'Finance row points to missing cleaning payment'}
            count={f.orphan_cleaning_link.count}
            sum={f.orphan_cleaning_link.sum}
            examples={f.orphan_cleaning_link.examples}
            testid="issue-orphan-cleaning"
          />
          <IssueList
            label={t('orphanBuildingLink') || 'Finance row points to missing building payment'}
            count={f.orphan_building_link.count}
            sum={f.orphan_building_link.sum}
            examples={f.orphan_building_link.examples}
            testid="issue-orphan-building"
          />
          <IssueList
            label={t('missingBankAccount') || 'Bank/Card income with no bank_account_id'}
            count={f.missing_bank_account.count}
            sum={f.missing_bank_account.sum}
            examples={f.missing_bank_account.examples}
            testid="issue-missing-bank"
          />
          <IssueList
            label={t('softDeletedStillLinked') || 'Soft-deleted finance row still has active payment link'}
            count={f.soft_deleted_still_linked.count}
            examples={f.soft_deleted_still_linked.examples}
            testid="issue-soft-deleted"
          />
        </div>
      </Section>

      {/* Cleaning anomalies */}
      <Section title={t('cleaningAnomalies') || 'Cleaning payments'} testid="section-cleaning-anomalies">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Stat label={t('total') || 'Total'} value={c.total} sub={fmtMoney(c.sum)} testid="stat-cleaning-total" />
          <Stat label={t('synced') || 'Synced'} value={c.synced.count} sub={fmtMoney(c.synced.sum)} tone="good" testid="stat-cleaning-synced" />
          <Stat label={t('reverseSyncFromFinance') || 'From Finance'} value={c.reverse_sync_from_finance.count} sub={fmtMoney(c.reverse_sync_from_finance.sum)} tone="info" testid="stat-cleaning-reverse" />
          <Stat label={t('pendingNoLink') || 'Pending (no link)'} value={c.pending_no_link.count} sub={fmtMoney(c.pending_no_link.sum)} tone={c.pending_no_link.count > 0 ? 'warn' : 'good'} testid="stat-cleaning-pending" />
        </div>
        <div className="space-y-2">
          <IssueList
            label={t('cleaningPendingNoLink') || 'Cleaning payments not synced to Finance'}
            count={c.pending_no_link.count}
            sum={c.pending_no_link.sum}
            examples={c.pending_no_link.examples}
            onResync={resyncOne}
            kind="cleaning"
            testid="issue-cleaning-pending"
          />
          <IssueList
            label={t('cleaningBrokenLink') || 'Cleaning payment links to deleted Finance row'}
            count={c.broken_link_deleted_income.count}
            sum={c.broken_link_deleted_income.sum}
            examples={c.broken_link_deleted_income.examples}
            onResync={resyncOne}
            kind="cleaning"
            testid="issue-cleaning-broken"
          />
          <IssueList
            label={t('cleaningMissingClient') || 'Cleaning payment has missing/unknown client_id'}
            count={c.missing_client.count}
            examples={c.missing_client.examples}
            testid="issue-cleaning-missing-client"
          />
        </div>
      </Section>

      {/* Building anomalies */}
      <Section title={t('buildingAnomalies') || 'Building payments'} testid="section-building-anomalies">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Stat label={t('total') || 'Total'} value={b.total} sub={fmtMoney(b.sum)} testid="stat-building-total" />
          <Stat label={t('synced') || 'Synced'} value={b.synced.count} sub={fmtMoney(b.synced.sum)} tone="good" testid="stat-building-synced" />
          <Stat label={t('pendingNoLink') || 'Pending (no link)'} value={b.pending_no_link.count} sub={fmtMoney(b.pending_no_link.sum)} tone={b.pending_no_link.count > 0 ? 'warn' : 'good'} testid="stat-building-pending" />
          <Stat label={t('brokenLinks') || 'Broken links'} value={b.broken_link_deleted_income.count} sub={fmtMoney(b.broken_link_deleted_income.sum)} tone={b.broken_link_deleted_income.count > 0 ? 'bad' : 'good'} testid="stat-building-broken" />
        </div>
        <div className="space-y-2">
          <IssueList
            label={t('buildingPendingNoLink') || 'Building payments not synced to Finance'}
            count={b.pending_no_link.count}
            sum={b.pending_no_link.sum}
            examples={b.pending_no_link.examples}
            onResync={resyncOne}
            kind="building"
            testid="issue-building-pending"
          />
          <IssueList
            label={t('buildingBrokenLink') || 'Building payment links to deleted Finance row'}
            count={b.broken_link_deleted_income.count}
            sum={b.broken_link_deleted_income.sum}
            examples={b.broken_link_deleted_income.examples}
            onResync={resyncOne}
            kind="building"
            testid="issue-building-broken"
          />
          <IssueList
            label={t('buildingMissingBuilding') || 'Building payment has missing/unknown building_id'}
            count={b.missing_building.count}
            examples={b.missing_building.examples}
            testid="issue-building-missing"
          />
        </div>
      </Section>

      {/* Duplicate clients (Phase 481) */}
      {dupes && (
        <Section title={t('duplicateClients') || 'Duplicate clients'} testid="section-duplicate-clients">
          {dupes.total_groups === 0 ? (
            <div className="border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center gap-2" data-testid="dupes-empty">
              <CheckCircle size={16} className="text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">{t('noDuplicateClients') || 'No duplicate clients detected.'}</span>
            </div>
          ) : (
            <div className="space-y-3" data-testid="dupes-list">
              <p className="text-xs text-zinc-500">
                {t('duplicateClientsHint') || 'Pairs of active clients that normalise to the same name (case-insensitive, ignoring common corporate suffixes like LTD / INC). Pick a survivor and click Merge — the source will be soft-deleted with a merged_into reference and all its jobs / invoices / payments / finance links will be re-pointed.'}
              </p>
              {dupes.duplicates.map(g => (
                <div key={g.normalized_name} className="border border-amber-200 bg-amber-50/30" data-testid={`dupe-group-${g.normalized_name}`}>
                  <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Warning size={14} className="text-amber-700" />
                      <span className="text-sm font-semibold text-amber-900">{g.normalized_name}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-white border border-amber-300">{g.size}</span>
                    </div>
                  </div>
                  <ul className="divide-y divide-amber-100">
                    {g.members.map((m, idx) => {
                      const isSurvivor = idx === 0; // backend pre-sorted biggest-first
                      return (
                        <li key={m.client_id} className={`px-3 py-2 flex items-center gap-3 ${isSurvivor ? 'bg-emerald-50/40' : 'bg-white'}`} data-testid={`dupe-member-${m.client_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-zinc-900 text-sm truncate">{m.name}</span>
                              {isSurvivor && (
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-300 bg-white text-emerald-700">{t('suggestedSurvivor') || 'Suggested survivor'}</span>
                              )}
                              {m.source === 'building_auto' && (
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-blue-200 bg-blue-50 text-blue-700">{t('buildingAuto') || 'Building Auto'}</span>
                              )}
                            </div>
                            <div className="text-[11px] text-zinc-500 font-mono">{m.client_id}</div>
                            <div className="text-[11px] text-zinc-600 mt-0.5 flex gap-3 flex-wrap">
                              <span>jobs: <strong>{m.jobs}</strong></span>
                              <span>invoices: <strong>{m.invoices}</strong></span>
                              <span>payments: <strong>{m.cleaning_payments}</strong></span>
                              <span>finance: <strong>{m.finance_incomes}</strong></span>
                              <span className="font-medium text-zinc-700">total: {m.total_records}</span>
                            </div>
                          </div>
                          {!isSurvivor && (
                            <button
                              onClick={() => mergeDuplicate(g.members[0].client_id, m.client_id, g.normalized_name)}
                              disabled={merging === g.normalized_name}
                              className="text-xs bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                              data-testid={`merge-${m.client_id}`}
                            >
                              {merging === g.normalized_name
                                ? (t('merging') || 'Merging…')
                                : (<>→ {t('mergeIntoSurvivor') || 'Merge into survivor'}</>)}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <footer className="text-center text-[11px] text-zinc-400">
        <a href="/finance" className="inline-flex items-center gap-1 hover:underline">
          <ArrowSquareOut size={11} /> {t('backToFinance') || 'Back to Finance'}
        </a>
      </footer>
    </div>
  );
};

export default SyncAudit;
